
  import { ref } from 'vue';
  import { Options, Vue } from 'vue-class-component';
  import { FilterMatchMode } from 'primevue/api';
  import Header from '@/components/Header.vue';
  import { AccountModule, AccountStore } from '@/store/account';
  import { NotificationService } from '@/services/notification-service';
  import {
    MailSenderModule,
    MailSenderStore,
    MailSchedulerOption,
    MailSchedulerOptions,
    MailSchedulerRoutine,
    MailSchedulerRunResult,
    MailSchedulerRunProgress,
    MailPreview
  } from '@/store/mailSender';

  @Options({
    components: {
      Header
    }
  })
  export default class MailSender extends Vue {
    private accountStore: AccountModule = AccountStore;
    private mailSenderStore: MailSenderModule = MailSenderStore;

    typeOptions = [
      { label: 'Newsletter', value: 'Newsletters' as MailSchedulerRoutine },
      { label: 'Announcement', value: 'Announcement' as MailSchedulerRoutine }
    ];

    options: MailSchedulerOptions = { newsletters: [], announcements: [] };
    selectedType: MailSchedulerRoutine | null = null;
    selectedIndex: number | null = null;
    send = false;

    loadingOptions = false;
    running = false;
    result: MailSchedulerRunResult | null = null;

    progress: MailSchedulerRunProgress | null = null;
    private pollTimer: number | null = null;

    preview: MailPreview | null = null;
    loadingPreview = false;
    previewCollapsed = false;

    sampleRecipients: string[] = ['laszlo.diewald@bkt-solutions.com'];
    sampleRecipient = 'laszlo.diewald@bkt-solutions.com';
    sendingSample = false;

    filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    get instanceOptions(): MailSchedulerOption[] {
      if (this.selectedType === 'Newsletters') {
        return this.options.newsletters;
      }
      if (this.selectedType === 'Announcement') {
        return this.options.announcements;
      }
      return [];
    }

    get canRun(): boolean {
      return !!this.selectedType && this.selectedIndex !== null && !this.running;
    }

    get canSendSample(): boolean {
      return !!this.selectedType && this.selectedIndex !== null && !!this.sampleRecipient?.trim() && !this.sendingSample;
    }

    get recipientRows() {
      return this.result ? this.result.recipients.map((email) => ({ email })) : [];
    }

    get progressPercent(): number {
      if (!this.progress) {
        return 0;
      }
      return Math.min(100, Math.max(0, Math.round(this.progress.percentComplete)));
    }

    get progressStatusText(): string {
      if (!this.progress) {
        return '';
      }

      switch (this.progress.status) {
        case 'Pending':
          return 'Starting\u2026';
        case 'Running':
          if (this.progress.currentRecipient) {
            return `Processing ${this.progress.currentRecipient}\u2026`;
          }
          return this.send ? 'Sending emails\u2026' : 'Collecting recipients\u2026';
        case 'Completed':
          return this.send ? 'All emails sent.' : 'Recipients collected.';
        case 'Failed':
          return this.progress.error || 'The run failed.';
        default:
          return '';
      }
    }

    onTypeChange() {
      this.selectedIndex = null;
      this.result = null;
      this.preview = null;
    }

    async onInstanceChange() {
      this.result = null;
      this.preview = null;
      this.previewCollapsed = false;

      if (this.selectedType === null || this.selectedIndex === null) {
        return;
      }

      this.loadingPreview = true;
      this.preview = await this.mailSenderStore.getPreview({
        routine: this.selectedType,
        index: this.selectedIndex
      });
      this.loadingPreview = false;
    }

    async onRun() {
      if (!this.canRun || this.selectedType === null || this.selectedIndex === null) {
        return;
      }

      this.stopPolling();
      this.running = true;
      this.previewCollapsed = true;
      this.result = null;
      this.progress = null;

      const started = await this.mailSenderStore.startRun({
        routine: this.selectedType,
        index: this.selectedIndex,
        send: this.send
      });

      if (!started) {
        this.running = false;
        return;
      }

      this.pollProgress(started.jobId);
    }

    async onSendSample() {
      if (!this.canSendSample || this.selectedType === null || this.selectedIndex === null) {
        return;
      }

      const email = this.sampleRecipient.trim();

      this.sendingSample = true;
      const sent = await this.mailSenderStore.sendSample({
        routine: this.selectedType,
        index: this.selectedIndex,
        email
      });
      this.sendingSample = false;

      // Keep typed addresses selectable for the rest of the session.
      if (sent && !this.sampleRecipients.includes(email)) {
        this.sampleRecipients = [...this.sampleRecipients, email];
      }
    }

    private pollProgress(jobId: string) {
      const poll = async () => {
        const progress = await this.mailSenderStore.getProgress(jobId);

        if (!progress) {
          this.stopPolling();
          this.running = false;
          NotificationService.NotifyError('Lost track of the mail sender run.');
          return;
        }

        this.progress = progress;

        if (progress.status === 'Completed') {
          this.stopPolling();
          this.running = false;
          this.result = progress.result;

          if (this.send && progress.result) {
            const total = progress.result.totalMails;
            NotificationService.NotifySuccess(`${total} email${total === 1 ? '' : 's'} sent.`);
          }
          return;
        }

        if (progress.status === 'Failed') {
          this.stopPolling();
          this.running = false;
          NotificationService.NotifyError(progress.error || 'Could not run the mail sender.');
          return;
        }

        this.pollTimer = window.setTimeout(poll, 750);
      };

      poll();
    }

    private stopPolling() {
      if (this.pollTimer !== null) {
        window.clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    }

    async mounted() {
      this.accountStore.initAuthDataFromLocalStorage();
      this.loadingOptions = true;
      this.options = await this.mailSenderStore.getOptions();
      this.loadingOptions = false;
    }

    beforeUnmount() {
      this.stopPolling();
    }
  }
